import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRightIcon, ChevronRightIcon } from 'lucide-react';
import type { BuilderTemplateId } from '../types/builder';
import { BOOK_DEMO_URL } from '../data/dgpt';
import { BUILDER_PATH, START_BUILDING_HREF } from '../data/customBuilder';
import { isBuilderTemplateId } from '../data/builderTemplates';
import { usePageMeta, canonicalUrl } from '../hooks/usePageMeta';
import { CtaButton } from '../components/ui/CtaButton';
import { useBuilder } from '../components/builder/BuilderApp/useBuilder';
import { BuilderApp } from '../components/builder/BuilderApp/BuilderApp';
import { FrontierArchitecture } from '../components/builder/sections/FrontierArchitecture';
import { StarterTemplates } from '../components/builder/sections/StarterTemplates';
import { AgentLibrary } from '../components/builder/sections/AgentLibrary';
import { GovernanceSection } from '../components/builder/sections/GovernanceSection';
import { FoundationSection } from '../components/builder/sections/FoundationSection';
import { DeploymentSection } from '../components/builder/sections/DeploymentSection';
const DESCRIPTION = 'DGPT Custom Workflow Builder: build custom due diligence applications with frontier AI and DiligenceGPT™ agents, data and governance — for investment, acquisitions, procurement and grants.';
const scrollToBuilder = () => document.getElementById('builder')?.scrollIntoView({
  behavior: 'smooth',
  block: 'start'
});
export function CustomWorkflowBuilder() {
  const [params] = useSearchParams();
  const requested = params.get('template');
  const builder = useBuilder(isBuilderTemplateId(requested) ? requested : 'procurement');
  usePageMeta({
    title: 'DGPT Custom Workflow Builder | Build Custom Due Diligence Applications',
    description: DESCRIPTION,
    path: BUILDER_PATH,
    keywords: ['custom due diligence software', 'due diligence workflow builder', 'AI diligence application builder', 'procurement due diligence workflow'],
    jsonLd: [{
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: canonicalUrl('/')
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'Solutions',
        item: canonicalUrl('/solutions')
      }, {
        '@type': 'ListItem',
        position: 3,
        name: 'Custom Workflow Builder',
        item: canonicalUrl(BUILDER_PATH)
      }]
    }]
  });
  const openTemplate = (id: BuilderTemplateId) => {
    builder.loadTemplate(id);
    scrollToBuilder();
  };
  const startButton = (placement: string, size: 'lg' | 'xl' = 'xl') => <CtaButton href={START_BUILDING_HREF} variant="violet" size={size} event="builder_cta_click" eventParams={{
    placement
  }} className="w-full sm:w-auto" data-id="element-1805" data-mp="gw8jjzj04ya1">
      Start Building
      <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1806" data-mp="pxggoe9s7t2j" />
    </CtaButton>;
  return <main id="main" data-id="element-1807" data-mp="pvlukwmrxuwv">
      <section aria-labelledby="builder-heading" className="dgpt-dark relative overflow-hidden bg-night" data-id="element-1808" data-mp="uw9sm29f7c0l">
        <div className="dgpt-night-grid sf-fade-mask pointer-events-none absolute inset-0" aria-hidden="true" data-id="element-1809" data-mp="2ff3maqi8ko6a" />
        <div className="relative mx-auto w-full max-w-[1320px] px-4 pb-20 pt-8 sm:px-6 lg:px-8 lg:pb-24 lg:pt-10" data-id="element-1810" data-mp="2aefva4u0c7c2">
          <nav aria-label="Breadcrumb" data-id="element-1811" data-mp="26eoy9jdw0uz3">
            <ol className="flex items-center gap-1.5 text-xs text-night-faint" data-id="element-1812" data-mp="26fqioegsmtth">
              <li data-id="element-1813" data-mp="21gvsuskes4zv"><Link to="/" className="hover:text-night-text" data-id="element-1814" data-mp="21gvuex024d1z">Home</Link></li>
              <li aria-hidden="true" data-id="element-1815" data-mp="2y4jywc774732"><ChevronRightIcon className="h-3 w-3" data-id="element-1816" data-mp="2y762y1uvo4gj" /></li>
              <li data-id="element-1817" data-mp="2u4j9jwik6bh8"><Link to="/solutions" className="hover:text-night-text" data-id="element-1818" data-mp="2u4rmfoeolgsf">Solutions</Link></li>
              <li aria-hidden="true" data-id="element-1819" data-mp="2pbq4t8n3b355"><ChevronRightIcon className="h-3 w-3" data-id="element-1820" data-mp="2pb6ykp6sy36y" /></li>
              <li aria-current="page" className="text-night-muted" data-id="element-1821" data-mp="2ke6swfdv72gd">Custom Workflow Builder</li>
            </ol>
          </nav>

          <div className="mt-12 grid gap-10 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] lg:items-end" data-id="element-1822" data-mp="3htuezrausu1p">
            <div data-id="element-1823" data-mp="3d0ripbeoz6ht">
              <p className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-night-muted" data-id="element-1824" data-mp="3d09bxkuel9vn">
                <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-violet" data-id="element-1825" data-mp="31loa1lhlh4st" />
                DGPT Custom Workflow Builder
              </p>
              <h1 id="builder-heading" className="sf-display on-dark mt-5 text-balance text-[38px] leading-[1.02] text-night-text sm:text-[54px] lg:text-[64px]" data-id="element-1826" data-mp="2nnzm24s8dhjj">
                Your Due Diligence. Your Rules. <span className="text-[#A995FF]" data-id="element-1827" data-mp="3gbc8meye5pcu">Built with AI.</span>
              </h1>
              <p className="mt-5 max-w-2xl text-lg font-medium text-night-text sm:text-xl" data-id="element-1828" data-mp="3kb4ay9x6yz9r">Turn frontier AI into your institution’s due diligence operating system.</p>
            </div>
            <div data-id="element-1829" data-mp="3bj2baduy5n0j">
              <p className="text-base leading-relaxed text-night-muted" data-id="element-1830" data-mp="3bij4ntaqyl1t">
                Build and customize your own due diligence applications using the world’s leading AI technologies and DiligenceGPT’s specialized agents, data, intelligence, and regulatory frameworks.
              </p>
              <p className="mt-3 text-base leading-relaxed text-night-muted" data-id="element-1831" data-mp="h8husgoi9hyc">
                From investment analysis to acquisitions, procurement, and grant evaluation, create secure, repeatable, auditable workflows tailored to your organization’s requirements.
              </p>
            </div>
          </div>

          <div className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center" data-id="element-1832" data-mp="m12jj3sk14v0">
            <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row" data-id="element-1833" data-mp="3uh454ocrohcp">
              {startButton('builder_hero')}
              <CtaButton href={BOOK_DEMO_URL} variant="outlineDark" size="xl" event="enterprise_demo_click" eventParams={{
              placement: 'builder_hero'
            }} className="w-full sm:w-auto" data-id="element-1834" data-mp="3qgk7ljoa7bq1">
                Book a Demo
              </CtaButton>
            </div>
            <p className="text-sm text-night-faint sm:ml-3" data-id="element-1835" data-mp="1jni44ega2ims">From a prompt to an institutional-grade diligence workflow.</p>
          </div>

          <div id="builder" className="mt-14 scroll-mt-24" data-id="element-1836" data-mp="1eoygm9m1cysb">
            <BuilderApp builder={builder} data-id="element-1837" data-mp="1mqnxypqmj7ad" />
          </div>
        </div>
      </section>

      <FrontierArchitecture data-id="element-1838" data-mp="1qqye2v84re2y" />
      <StarterTemplates onOpen={openTemplate} data-id="element-1839" data-mp="1qq6gxfyvx8ak" />
      <AgentLibrary onAdd={names => {
      builder.addAgents(names);
      scrollToBuilder();
    }} data-id="element-1840" data-mp="1vqks36vs8yct" />
      <GovernanceSection data-id="element-1841" data-mp="20pxclkz6ltc8" />
      <FoundationSection onStartFromDgpt={() => {
      builder.startFromDgpt();
      scrollToBuilder();
    }} data-id="element-1842" data-mp="20p5gm91o9tlf" />
      <DeploymentSection data-id="element-1843" data-mp="180zv3k2so0kj" />

      <section aria-labelledby="builder-final-heading" className="dgpt-dark relative overflow-hidden bg-night" data-id="element-1844" data-mp="180gkypjdygh2">
        <div className="dgpt-night-grid sf-fade-mask pointer-events-none absolute inset-0" aria-hidden="true" data-id="element-1845" data-mp="1cz1ph15o7mbb" />
        <div className="relative mx-auto flex w-full max-w-[1000px] flex-col items-center px-4 py-24 text-center sm:px-6 lg:py-32" data-id="element-1846" data-mp="1hrl29vwjadf2">
          <h2 id="builder-final-heading" className="sf-display on-dark text-balance text-[34px] leading-[1.04] text-night-text sm:text-[52px]" data-id="element-1847" data-mp="k9l0yel68pkd">
            Your Due Diligence. Your Rules. Built with AI.
          </h2>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-night-muted sm:text-lg" data-id="element-1848" data-mp="crwje37lhvph">
            From investment decisions to procurement, acquisitions, and grantmaking, build and operate custom diligence applications using frontier AI and institutional-grade intelligence.
          </p>
          <div className="mt-9 flex w-full flex-col justify-center gap-3 sm:w-auto sm:flex-row" data-id="element-1849" data-mp="3uxj4v07kco1t">
            {startButton('builder_final')}
            <CtaButton href={BOOK_DEMO_URL} variant="outlineDark" size="xl" event="enterprise_demo_click" eventParams={{
            placement: 'builder_final'
          }} className="w-full sm:w-auto" data-id="element-1850" data-mp="3q610e26c05c3">
              Book an Enterprise Demo
            </CtaButton>
          </div>
          <p className="mt-12 text-sm font-semibold text-night-text" data-id="element-1851" data-mp="3b9ikw4r76z76">DiligenceGPT™ — AI That Masters Due Diligence.</p>
          <p className="mt-1 text-sm text-night-muted" data-id="element-1852" data-mp="377f20v278sbv">Leave no stone unturned.</p>
        </div>
      </section>
    </main>;
}